import React, { useState, useEffect, useMemo } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

type TimeFilter = 'today' | '7days' | 'month' | 'all';
type SortOrder = 'highest' | 'lowest';

interface UserRecord {
  id: string;
  email: string;
  username: string;
  signUpDate: Date | null;
  challengesCompletedCount: number;
  approvedCount: number;
  pendingCount: number;
  rejectedCount: number;
  totalCount: number;
}

interface ChartDataPoint {
  date: string;
  users: number;
}

export default function UserData() {
  const [timeFilter, setTimeFilter] = useState<TimeFilter>('7days');
  const [loading, setLoading] = useState(true);
  const [totalUsers, setTotalUsers] = useState(0);
  const [chartData, setChartData] = useState<ChartDataPoint[]>([]);
  const [userRecords, setUserRecords] = useState<UserRecord[]>([]);
  const [sortOrder, setSortOrder] = useState<SortOrder>('highest');

  const getTimeRange = (filter: TimeFilter) => {
    const now = new Date();
    switch (filter) {
      case 'today':
        return { start: new Date(now.setHours(0, 0, 0, 0)), end: new Date() };
      case '7days':
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        return { start: sevenDaysAgo, end: new Date() };
      case 'month':
        const monthAgo = new Date();
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        return { start: monthAgo, end: new Date() };
      case 'all':
        return { start: null, end: null };
      default:
        return { start: null, end: null };
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const timeRange = getTimeRange(timeFilter);

        const usersSnapshot = await getDocs(collection(db, 'users'));
        setTotalUsers(usersSnapshot.docs.length);

        const toDate = (v: unknown): Date | null => {
          if (!v) return null;
          if (v instanceof Date) return isNaN(v.getTime()) ? null : v;
          if (typeof v === 'object' && v !== null && 'toDate' in v && typeof (v as { toDate: () => Date }).toDate === 'function') {
            const d = (v as { toDate: () => Date }).toDate();
            return isNaN(d.getTime()) ? null : d;
          }
          const d = new Date(v as string | number);
          return isNaN(d.getTime()) || d.getTime() === 0 ? null : d;
        };

        const allUserDates = usersSnapshot.docs.map(docSnap => {
          const d = docSnap.data();
          return toDate(d.createdAt) ?? toDate(d.legalAcceptedAt) ?? toDate(d.signUpDate) ?? toDate(d.registeredAt);
        }).filter((d): d is Date => d != null);

        let chartUsers = allUserDates;
        if (timeRange.start) chartUsers = chartUsers.filter(d => d >= timeRange.start!);
        if (timeRange.end) chartUsers = chartUsers.filter(d => d <= timeRange.end!);
        const chartMap = new Map<string, number>();
        chartUsers.forEach(date => {
          const key = date.toISOString().split('T')[0];
          chartMap.set(key, (chartMap.get(key) || 0) + 1);
        });
        const sortedChart = Array.from(chartMap.entries())
          .map(([date, users]) => ({ date, users }))
          .sort((a, b) => a.date.localeCompare(b.date))
          .map(item => ({
            ...item,
            date: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          }));
        setChartData(sortedChart);

        const submissionsSnapshot = await getDocs(collection(db, 'submissions'));
        const byUser: Record<string, { approved: number; pending: number; rejected: number; total: number }> = {};
        submissionsSnapshot.docs.forEach(docSnap => {
          const d = docSnap.data();
          const uid = (d.userId || d.user_id || '').trim();
          if (!uid) return;
          if (!byUser[uid]) byUser[uid] = { approved: 0, pending: 0, rejected: 0, total: 0 };
          byUser[uid].total += 1;
          const status = (d.status || '').toLowerCase();
          if (status === 'approved') byUser[uid].approved += 1;
          else if (status === 'pending') byUser[uid].pending += 1;
          else if (status === 'rejected') byUser[uid].rejected += 1;
        });

        // Same logic as challenges page: a completion is when user's progress is 'rewarded'
        // (that's when the Cloud Function increments challenge.analytics.completionsCount).
        // Only count progress for challenges that exist in the challenges collection.
        const challengesSnapshot = await getDocs(collection(db, 'challenges'));
        const challengeIds = new Set(challengesSnapshot.docs.map((d) => d.id));
        const challengesProgressSnapshot = await getDocs(collection(db, 'userChallengeProgress'));
        const challengesCompletedByUser: Record<string, number> = {};
        challengesProgressSnapshot.docs.forEach(docSnap => {
          const d = docSnap.data();
          const uid = (d.userId || d.uid || '').trim();
          if (!uid) return;
          const challengeId = (d.challengeId || '').trim();
          if (!challengeIds.has(challengeId)) return;
          if ((d.status || '').toLowerCase() === 'rewarded') {
            challengesCompletedByUser[uid] = (challengesCompletedByUser[uid] || 0) + 1;
          }
        });

        const records: UserRecord[] = usersSnapshot.docs.map(docSnap => {
          const d = docSnap.data();
          const counts = byUser[docSnap.id] || { approved: 0, pending: 0, rejected: 0, total: 0 };
          const email = String(d.email ?? d.emailAddress ?? d.userEmail ?? '').trim() || '—';
          const username = String(d.username ?? d.displayName ?? d.name ?? '').trim() || '—';
          const signUpDate = toDate(d.createdAt) ?? toDate(d.legalAcceptedAt) ?? toDate(d.signUpDate) ?? toDate(d.registeredAt);
          return {
            id: docSnap.id,
            email,
            username,
            signUpDate,
            challengesCompletedCount: challengesCompletedByUser[docSnap.id] ?? 0,
            approvedCount: counts.approved,
            pendingCount: counts.pending,
            rejectedCount: counts.rejected,
            totalCount: counts.total,
          };
        });
        setUserRecords(records);
      } catch (error) {
        console.error('Error loading user data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [timeFilter]);

  const sortedRecords = useMemo(() => {
    const list = [...userRecords];
    if (sortOrder === 'highest') {
      list.sort((a, b) => b.totalCount - a.totalCount);
    } else {
      list.sort((a, b) => a.totalCount - b.totalCount);
    }
    return list;
  }, [userRecords, sortOrder]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-600">Loading user data...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">User Data</h1>
        <div className="flex gap-2">
          {(['today', '7days', 'month', 'all'] as TimeFilter[]).map((filter) => (
            <button
              key={filter}
              onClick={() => setTimeFilter(filter)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                timeFilter === filter
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {filter === '7days' ? '7 Days' : filter === 'all' ? 'All Time' : filter.charAt(0).toUpperCase() + filter.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Stat box */}
      <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm text-center max-w-xs">
          <div className="text-2xl font-bold text-blue-600">{totalUsers.toLocaleString()}</div>
          <div className="text-sm text-gray-600 mt-1">Number of Users</div>
        </div>
      </div>

      {/* User growth chart - same design as dashboard */}
      <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">User Growth Over Time</h2>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
            <XAxis dataKey="date" stroke="#9ca3af" tick={{ fill: '#9ca3af' }} />
            <YAxis stroke="#9ca3af" tick={{ fill: '#9ca3af' }} />
            <Tooltip contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px', color: '#f9fafb' }} labelStyle={{ color: '#f9fafb' }} itemStyle={{ color: '#f9fafb' }} />
            <Legend />
            <Line type="monotone" dataKey="users" stroke="#00e3ff" name="New users" />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Users by submission activity</h2>
          <div className="flex items-center gap-2">
            <label htmlFor="sort-order" className="text-sm text-gray-600">Sort by total submissions:</label>
            <select
              id="sort-order"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value as SortOrder)}
              className="px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              <option value="highest">Highest first</option>
              <option value="lowest">Lowest first</option>
            </select>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Email</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Username</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Sign up date</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase">Challenges completed</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase">Approved</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase">Pending</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase">Rejected</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {sortedRecords.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-900">{user.email}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{user.username}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {user.signUpDate && !isNaN(user.signUpDate.getTime())
                      ? user.signUpDate.toLocaleDateString()
                      : '—'}
                  </td>
                  <td className="px-4 py-3 text-sm text-right text-gray-900">{user.challengesCompletedCount}</td>
                  <td className="px-4 py-3 text-sm text-right text-gray-900">{user.approvedCount}</td>
                  <td className="px-4 py-3 text-sm text-right text-gray-900">{user.pendingCount}</td>
                  <td className="px-4 py-3 text-sm text-right text-gray-900">{user.rejectedCount}</td>
                  <td className="px-4 py-3 text-sm text-right font-medium text-gray-900">{user.totalCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {sortedRecords.length === 0 && (
          <p className="px-4 py-6 text-gray-500 text-center">No users found.</p>
        )}
      </div>
    </div>
  );
}
